import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, User, FileText, Download, Share2, Copy, Check, Facebook, Linkedin, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Social share icons
const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const XTwitterIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const PinterestIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
    <path d="M12 0a12 12 0 0 0-4.373 23.178c-.07-.937-.134-2.377.028-3.401.147-.928.948-5.908.948-5.908s-.242-.484-.242-1.2c0-1.124.652-1.963 1.464-1.963.69 0 1.023.518 1.023 1.14 0 .695-.443 1.734-.671 2.698-.19.804.404 1.46 1.198 1.46 1.438 0 2.543-1.516 2.543-3.702 0-1.935-1.39-3.289-3.377-3.289-2.301 0-3.653 1.726-3.653 3.511 0 .695.268 1.44.603 1.844.066.08.076.15.056.232-.062.256-.199.804-.226.916-.036.148-.119.18-.275.108-1.027-.478-1.67-1.98-1.67-3.187 0-2.593 1.884-4.975 5.433-4.975 2.852 0 5.068 2.032 5.068 4.749 0 2.833-1.786 5.113-4.265 5.113-.833 0-1.617-.433-1.885-.944l-.512 1.954c-.186.715-.689 1.612-1.025 2.16A12 12 0 1 0 12 0z"/>
  </svg>
);

// Helper to get file extension icon
const getFileIcon = (filename) => {
  const ext = filename.split('.').pop()?.toLowerCase();
  const icons = {
    pdf: '📄',
    doc: '📝',
    docx: '📝',
    xls: '📊',
    xlsx: '📊',
    ppt: '📽️',
    pptx: '📽️',
    ai: '🎨',
    psd: '🖼️',
    png: '🖼️',
    jpg: '🖼️',
    jpeg: '🖼️',
    txt: '📃'
  };
  return icons[ext] || '📎';
};

// Helper to force download with original extension
const handleDownload = async (doc) => {
  try {
    const response = await fetch(doc.url);
    const blob = await response.blob();
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.name; // Use original filename with extension
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  } catch (error) {
    // Fallback: open in new tab
    window.open(doc.url, '_blank');
  }
};

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linkCopied, setLinkCopied] = useState(false);

  // Default logo for sharing
  const DEFAULT_LOGO = "https://customer-assets.emergentagent.com/job_enrigo-creative/artifacts/19zqfisl_EA%20Logo.png";

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/blog/${slug}`);
      setPost(response.data);
      
      // Update page meta tags for social sharing
      updateMetaTags(response.data);
    } catch (error) {
      console.error('Error fetching post:', error);
    } finally {
      setLoading(false);
    }
  };

  // Update meta tags dynamically for social sharing
  const updateMetaTags = (postData) => {
    document.title = `${postData.title} | Enrigo Studio`;
    
    // Update or create OG meta tags
    const updateMeta = (property, content) => {
      let meta = document.querySelector(`meta[property="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    
    const shareImage = postData.featuredImage || DEFAULT_LOGO;
    const shareUrl = window.location.href;
    const shareDescription = postData.excerpt || postData.content?.substring(0, 160) || '';
    
    updateMeta('og:title', postData.title);
    updateMeta('og:description', shareDescription);
    updateMeta('og:image', shareImage);
    updateMeta('og:url', shareUrl);
    updateMeta('og:type', 'article');
    
    // Twitter Card
    updateMeta('twitter:card', 'summary_large_image');
    updateMeta('twitter:title', postData.title);
    updateMeta('twitter:description', shareDescription);
    updateMeta('twitter:image', shareImage);
  };

  // Share functions
  const getPostShareUrl = () => window.location.href;
  
  const getShareText = () => {
    return `${post.title} - ${post.excerpt?.substring(0, 100) || ''}...`;
  };

  const copyPostLink = async () => {
    const url = getPostShareUrl();
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      toast({ title: "Link Copied!", description: "Post link copied to clipboard" });
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setLinkCopied(true);
      toast({ title: "Link Copied!", description: "Post link copied to clipboard" });
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  const shareOnWhatsApp = () => {
    const url = getPostShareUrl();
    const text = getShareText();
    window.open(`https://wa.me/?text=${encodeURIComponent(text + '\n\n' + url)}`, '_blank');
  };

  const shareOnFacebook = () => {
    const url = getPostShareUrl();
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
  };

  const shareOnTwitter = () => {
    const url = getPostShareUrl();
    const text = getShareText();
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
  };

  const shareOnLinkedIn = () => {
    const url = getPostShareUrl();
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
  };

  const shareOnPinterest = () => {
    const url = getPostShareUrl();
    const image = post.featuredImage || DEFAULT_LOGO;
    const description = getShareText();
    window.open(`https://pinterest.com/pin/create/button/?url=${encodeURIComponent(url)}&media=${encodeURIComponent(image)}&description=${encodeURIComponent(description)}`, '_blank');
  };

  const shareByEmail = () => {
    const url = getPostShareUrl();
    const subject = post.title;
    const body = `${getShareText()}\n\nRead more: ${url}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white text-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F4B400] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading article...</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-3xl font-bold mb-4">Article Not Found</h2>
          <Button onClick={() => navigate('/blog')} className="bg-[#F4B400] text-black hover:bg-[#d49f00]">
            Back to Blog
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white text-black pt-24">
      {/* Hero Image */}
      <div className="relative h-96 overflow-hidden">
        <img 
          src={post.featuredImage} 
          alt={post.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-40"></div>
      </div>

      {/* Article Content */}
      <article className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <Button
              onClick={() => navigate('/blog')}
              variant="outline"
              className="mb-8 border-[#F4B400] text-[#F4B400] hover:bg-[#F4B400] hover:text-white"
            >
              <ArrowLeft className="mr-2" size={16} />
              Back to Blog
            </Button>

            <Badge className="bg-[#F4B400] text-black hover:bg-[#d49f00] mb-4">
              {post.category}
            </Badge>

            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">{post.title}</h1>

            <div className="flex items-center justify-between gap-6 text-gray-600 mb-8 pb-8 border-b">
              <div className="flex items-center gap-6">
                <div className="flex items-center">
                  <User size={18} className="mr-2 text-[#F4B400]" />
                  <span>{post.authorId?.name || 'Author'}</span>
                </div>
                <div className="flex items-center">
                  <Calendar size={18} className="mr-2 text-[#F4B400]" />
                  <span>{new Date(post.publishedAt || post.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                </div>
              </div>
              
              {/* Share Button */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="border-[#F4B400] text-[#F4B400] hover:bg-[#F4B400] hover:text-black"
                  >
                    <Share2 size={18} className="mr-2" />
                    Share
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={copyPostLink} className="cursor-pointer">
                    {linkCopied ? (
                      <><Check className="mr-2 h-4 w-4 text-green-500" />Link Copied!</>
                    ) : (
                      <><Copy className="mr-2 h-4 w-4" />Copy Link</>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={shareOnWhatsApp} className="cursor-pointer">
                    <span className="mr-2 text-green-500"><WhatsAppIcon /></span>WhatsApp
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={shareOnFacebook} className="cursor-pointer">
                    <Facebook className="mr-2 h-4 w-4 text-blue-600" />Facebook
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={shareOnTwitter} className="cursor-pointer">
                    <span className="mr-2"><XTwitterIcon /></span>X (Twitter)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={shareOnLinkedIn} className="cursor-pointer">
                    <Linkedin className="mr-2 h-4 w-4 text-blue-700" />LinkedIn
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={shareOnPinterest} className="cursor-pointer">
                    <span className="mr-2 text-red-600"><PinterestIcon /></span>Pinterest
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={shareByEmail} className="cursor-pointer">
                    <Mail className="mr-2 h-4 w-4" />Email
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="prose prose-lg max-w-none">
              <p className="text-xl text-gray-700 mb-8 leading-relaxed font-medium">{post.excerpt}</p>
              
              {/* Video embed if available */}
              {post.videoUrl && (
                <div className="mb-8">
                  <div className="relative w-full pt-[56.25%] bg-black rounded-lg overflow-hidden">
                    {post.videoUrl.includes('youtube.com') || post.videoUrl.includes('youtu.be') ? (
                      <iframe
                        className="absolute top-0 left-0 w-full h-full"
                        src={post.videoUrl.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                        title={post.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    ) : post.videoUrl.includes('vimeo.com') ? (
                      <iframe
                        className="absolute top-0 left-0 w-full h-full"
                        src={post.videoUrl.replace('vimeo.com/', 'player.vimeo.com/video/')}
                        title={post.title}
                        frameBorder="0"
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    ) : (
                      <video
                        className="absolute top-0 left-0 w-full h-full"
                        controls
                        src={post.videoUrl}
                      >
                        Your browser does not support the video tag.
                      </video>
                    )}
                  </div>
                </div>
              )}
              
              {/* Content - supports HTML */}
              {post.contentType === 'html' ? (
                <div 
                  className="text-gray-700 leading-relaxed blog-content"
                  dangerouslySetInnerHTML={{ __html: post.content }}
                />
              ) : (
                <div className="text-gray-700 leading-relaxed space-y-6">
                  {post.content.split('\n\n').map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>
              )}
            </div>

            {/* Reference Documents */}
            {post.referenceDocuments && post.referenceDocuments.length > 0 && (
              <div className="mt-12 p-6 bg-gray-50 rounded-lg border">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <FileText className="text-[#F4B400]" size={20} />
                  Reference Documents
                </h3>
                <div className="space-y-3">
                  {post.referenceDocuments.map((doc, index) => (
                    <button 
                      key={index}
                      onClick={() => handleDownload(doc)}
                      className="flex items-center justify-between p-3 bg-white rounded-lg border hover:border-[#F4B400] hover:shadow-sm transition-all group w-full text-left"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{getFileIcon(doc.name)}</span>
                        <span className="font-medium text-gray-700">{doc.name}</span>
                      </div>
                      <Download className="text-gray-400 group-hover:text-[#F4B400]" size={18} />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Author Box */}
            <div className="mt-12 p-6 bg-gray-100 rounded-lg">
              <h3 className="font-bold text-lg mb-2">About the Author</h3>
              <p className="text-gray-600">
                <strong>{post.authorId?.name || 'Author'}</strong> is a contributor to the Enrigo blog, sharing insights and expertise 
                in {post.category.toLowerCase()}.
              </p>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
};

export default BlogPost;